import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Dropdown, Avatar, Tag, Space, Drawer } from 'antd';
import {
  HomeOutlined,
  ReadOutlined,
  DashboardOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  ApartmentOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { toast } from '../../utils/toast.js';
import './navbar.css';

export function Navbar() {
  const { user, isAuthenticated, isAdmin, isInstructor, canManageContent, logout } = useAuth();
  const roleLabel = isAdmin ? 'Quản trị viên' : isInstructor ? 'Giảng viên' : 'Học viên';
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  const handleLogout = () => {
    setDrawerOpen(false);
    logout();
    toast.success('Đã đăng xuất khỏi hệ thống!');
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'user-info',
      disabled: true,
      label: (
        <div style={{ padding: '4px 0', color: '#1e293b' }}>
          <div style={{ fontWeight: 600 }}>{user?.fullName}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{user?.pharmacyName || user?.email}</div>
          <Tag color={isAdmin ? 'purple' : isInstructor ? 'blue' : 'green'} style={{ marginTop: 4, fontSize: 11 }}>
            {isAdmin ? 'Quản Trị Viên' : isInstructor ? 'Giảng Viên' : 'Dược Sĩ / Học Viên'}
          </Tag>
        </div>
      ),
    },
    { type: 'divider' },
    ...(canManageContent
      ? [
        {
          key: 'admin-cms',
          icon: <SettingOutlined />,
          label: 'Trang Quản Trị Hệ Thống',
          onClick: () => navigate('/admin'),
        },
      ]
      : []),
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Khóa học của tôi',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: 'profile',
      icon: <IdcardOutlined />,
      label: 'Hồ sơ cá nhân',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      danger: true,
      label: 'Đăng xuất',
      onClick: handleLogout,
    },
  ];

  const isCurrent = (path) => location.pathname === path;

  return (
    <header
      style={{
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      <div
        className="navbar-header-inner"
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Brand Logo */}
        <Link
          to={canManageContent ? '/admin' : '/'}
          className="navbar-brand-link"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textDecoration: 'none',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 8,
              background: '#005b2f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              boxShadow: '0 2px 6px rgba(0, 91, 47, 0.25)',
            }}
          >
            <img
              src="/images/the-thao-pharma-logo.jpg"
              alt="Logo Thế Thảo Pharma"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
          <div className="navbar-brand-text">
            <span
              className="navbar-brand-title"
              style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px', display: 'block', marginBottom: '5px' }}
            >
              Hệ Thống Đào Tạo Nhà Thuốc Thực Chiến
            </span>
            <div className="navbar-brand-subtitle" style={{ fontSize: 11, color: '#64748b', marginTop: -2, fontWeight: 500 }}>
              Đơn vị phát hành: Thế Thảo Pharma
            </div>
          </div>
        </Link>

        {/* Navigation Menu (Desktop) */}
        <nav className="navbar-desktop-links" style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: '100px' }}>
          {canManageContent && (
            <Link
              to="/admin"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 600,
                color: isCurrent('/admin') ? '#7e22ce' : '#6b21a8',
                background: isCurrent('/admin') ? '#faf5ff' : 'transparent',
              }}
            >
              <SettingOutlined /> Quản Trị Hệ Thống
            </Link>
          )}

          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: 6,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
              color: isCurrent('/') ? '#005537' : '#475569',
              background: isCurrent('/') ? '#f0fdf4' : 'transparent',
            }}
          >
            <HomeOutlined /> Trang Chủ
          </Link>

          <Link
            to="/courses"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: 6,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
              color: isCurrent('/courses') ? '#005537' : '#475569',
              background: isCurrent('/courses') ? '#f0fdf4' : 'transparent',
            }}
          >
            <ReadOutlined /> Khóa Học Đào Tạo
          </Link>

          <Link
            to="/tools"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: 6,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
              color: location.pathname.startsWith('/tool') ? '#005537' : '#475569',
              background: location.pathname.startsWith('/tool') ? '#f0fdf4' : 'transparent',
            }}
          >
            <ApartmentOutlined /> Công Cụ Thực Chiến
          </Link>

          {isAuthenticated && (
            <Link
              to="/dashboard"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 600,
                color: isCurrent('/dashboard') ? '#005537' : '#475569',
                background: isCurrent('/dashboard') ? '#f0fdf4' : 'transparent',
              }}
            >
              <DashboardOutlined /> Tiến Độ Của Tôi
            </Link>
          )}
        </nav>

        {/* Auth / Profile Area (Desktop) */}
        <div className="navbar-desktop-auth">
          {isAuthenticated ? (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow trigger={['click']}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 6,
                  transition: 'background 0.2s',
                  marginRight: '10px'
                }}
              >
                <Avatar
                  src={user?.avatar}
                  icon={!user?.avatar && <UserOutlined />}
                  style={{ backgroundColor: canManageContent ? '#7e22ce' : '#0284c7' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                    {user?.fullName?.split(' ').slice(-2).join(' ')}
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {roleLabel}
                  </span>
                </div>
              </div>
            </Dropdown>
          ) : (
            <Space size={8}>
              <Button type="text" onClick={() => navigate('/login')}>
                Đăng nhập
              </Button>
              <Button type="primary" onClick={() => navigate('/register')}>
                Đăng ký
              </Button>
            </Space>
          )}
        </div>

      </div>

      {/* Account panel (Mobile, opened from bottom tab bar) */}
      <Drawer
        title={
          <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
            {isAuthenticated ? 'Tài khoản của tôi' : 'Đăng nhập / Đăng ký'}
          </span>
        }
        placement="bottom"
        size={isAuthenticated ? 320 : 220}
        open={drawerOpen}
        onClose={closeDrawer}
        styles={{ body: { padding: '8px 20px 24px' } }}
      >
        {isAuthenticated ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px' }}>
              <Avatar
                src={user?.avatar}
                icon={!user?.avatar && <UserOutlined />}
                style={{ backgroundColor: canManageContent ? '#7e22ce' : '#0284c7' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{user?.fullName}</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  {roleLabel}
                </span>
              </div>
            </div>

            <Link
              to="/dashboard"
              onClick={closeDrawer}
              className={`navbar-drawer-link${isCurrent('/dashboard') ? ' is-active' : ''}`}
            >
              <DashboardOutlined /> Tiến Độ Của Tôi
            </Link>

            <Link
              to="/profile"
              onClick={closeDrawer}
              className={`navbar-drawer-link${isCurrent('/profile') ? ' is-active' : ''}`}
            >
              <IdcardOutlined /> Hồ Sơ Cá Nhân
            </Link>

            {canManageContent && (
              <Link
                to="/admin"
                onClick={closeDrawer}
                className={`navbar-drawer-link${isCurrent('/admin') ? ' is-active' : ''}`}
              >
                <SettingOutlined /> Quản Trị Hệ Thống
              </Link>
            )}

            <Button danger block icon={<LogoutOutlined />} onClick={handleLogout} style={{ marginTop: 14 }}>
              Đăng xuất
            </Button>
          </>
        ) : (
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            <Button
              block
              onClick={() => {
                closeDrawer();
                navigate('/login');
              }}
            >
              Đăng nhập
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                closeDrawer();
                navigate('/register');
              }}
            >
              Đăng ký
            </Button>
          </Space>
        )}
      </Drawer>

      {/* Bottom tab bar (Mobile) */}
      <nav className="navbar-bottom-tabs">
        <Link
          to="/"
          className={`navbar-tab${isCurrent('/') ? ' is-active' : ''}`}
        >
          <HomeOutlined />
          <span>Trang Chủ</span>
        </Link>
        <Link
          to="/courses"
          className={`navbar-tab${isCurrent('/courses') ? ' is-active' : ''}`}
        >
          <ReadOutlined />
          <span>Khóa Học</span>
        </Link>
        <Link
          to="/tools"
          className={`navbar-tab${location.pathname.startsWith('/tool') ? ' is-active' : ''}`}
        >
          <ApartmentOutlined />
          <span>Công Cụ</span>
        </Link>
        <button
          type="button"
          className={`navbar-tab navbar-tab-btn${drawerOpen ? ' is-active' : ''}`}
          onClick={() => setDrawerOpen(true)}
        >
          {isAuthenticated ? (
            <Avatar
              size={20}
              src={user?.avatar}
              icon={!user?.avatar && <UserOutlined />}
              style={{ backgroundColor: canManageContent ? '#7e22ce' : '#0284c7' }}
            />
          ) : (
            <UserOutlined />
          )}
          <span>{isAuthenticated ? 'Tài Khoản' : 'Đăng Nhập'}</span>
        </button>
      </nav>
    </header>
  );
}
